import { useState, useEffect, ReactNode, useRef, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useMutation,
  useQueryClient,
  useQuery
} from '@tanstack/react-query';
import * as amplitude from '@amplitude/analytics-browser';
import { CustomProvider, DatePicker } from 'rsuite';
import { arEG, enUS } from 'rsuite/locales';
import {
  Button,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  Modal,
  SkeletonBase
} from '@unified-checkout/design-system';
import { SECONDARY_FILTERS, shouldDisableZatcaMonth } from './utils';
import {
  Plus,
  ClipboardListCheck,
  AnimatedSpinner,
} from '@unified-checkout/design-system/icons/index';
import { SuccessHeader } from '@unified-checkout/design-system/icons/state_icons/index'
import type { ISharedEmail } from '@app/dashboard/types/innerLayout/reports/modals/share';
import type { IPreDefinedRange } from 'libs/design-system/src/types/date_range_picker';
import { DateRangePickerButton } from 'libs/design-system/src/components/DateRangePicker/DateRangePickerButton';
import { useClickOutside } from 'libs/design-system/src/hooks/useClickOutside';
import {
  TCustomReportData,
  TGenerateReportAPIParams,
  TTypeSelectorOptions,
  TOption,
  TSecondaryFilters,
  TCustomReportColumnsResponse,
  TTransformedColumn,
  TGenerateReportProps
} from '@app/dashboard/types/innerLayout/reports';
import type { TAppliedRanges } from '@app/dashboard/types/innerLayout/date-ranges';
import type { TReportTemplateTypes } from '@app/dashboard/types/shared/modals/reports-template';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { MODAL_TYPES } from '@app/dashboard/constants/reports';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import {
  SYSTEM_EVENTS_GROUPING_CODES,
  SYSTEM_EVENTS_SRC_FEATURES
} from '@app/dashboard/constants/tracking_events/system';
import {
  REPORTS_STATEMENTS_EVENTS,
  REPORTS_STATEMENTS_EVENTS_PARAMS,
  REPORTS_STATEMENTS_EVENTS_VALUES,
  TReportGenerationPayload,
} from '@app/dashboard/constants/tracking_events/reports-statements';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import { notify } from '@app/dashboard/utils/notifier';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { useLangStore } from '@app/dashboard/store/lang';
import { exportReport, getCustomReportColumns, getReportSecondaryFilters } from '@app/dashboard/services/inner_layout/reports';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { getFormattedDates } from '@app/dashboard/utils/getFormattedDates';
import { mainPreDefinedRanges } from '@app/dashboard/utils/dateRangePicker';
import { onToasterShowHandler } from '@app/dashboard/utils/tracking/onToasterShowHandler';
import SecondaryFilters from './SecondaryFilters';
import { RenderShareModalBody } from './Modals/ModalBodyContent';
import { RenderCustomReportsModalBody } from './Modals/ModalBodyContent';
import ReportsTemplate from '../../shared/modals/ReportsTemplate';
import DateRanges from '../DateRanges';
import styles from './generateReport.module.scss';

export const GenerateReport = ({
  typeOptions,
  isReportTypesLoading,
  isReportTypesError,
  reportTypesError,
  isReportDownloadError,
  reportDownloadError,
  headerDateRange
}: TGenerateReportProps) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();
  const queryClient = useQueryClient();
  const containerRef = useRef<HTMLDialogElement>(null);


  const { isLive } = useTestLiveModeStore();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const mainDefaultPreDefinedRange: IPreDefinedRange | undefined =
    mainPreDefinedRanges(t)?.find((range) => range.default);

  const appliedRangesInitialState: TAppliedRanges = {
    index: mainDefaultPreDefinedRange?.index,
    name: mainDefaultPreDefinedRange?.name,
    start: mainDefaultPreDefinedRange?.selectionRange?.[0],
    end: mainDefaultPreDefinedRange?.selectionRange?.[1],
  };

  const customReportDataInitialState: TCustomReportData = {
    report_name: '',
    selected_columns: [] as TOption[],
    save_template: false,
    ranges: { ...appliedRangesInitialState },
  };

  const [type, setType] = useState<TOption>();
  const [secondaryFilters, setSecondaryFilters] = useState<TSecondaryFilters>({});
  const [isCustomReportModalOpen, setIsCustomReportModalOpen] = useState(false);
  const [customReportData, setCustomReportData] = useState<TCustomReportData>(customReportDataInitialState);
  const [appliedRanges, setAppliedRanges] = useState<TAppliedRanges>(appliedRangesInitialState);
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
  const [isCustomReportValid, setIsCustomReportValid] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [sharedList, setSharedList] = useState<ISharedEmail[]>([]);
  const [isReportsTemplateOpen, setIsReportsTemplateOpen] = useState(false);  

  const generationStartTimeRef = useRef<number | null>(null);
  const generationPayloadRef = useRef<TReportGenerationPayload | null>(null);
  const monthPickerBtnRef = useRef<any>(null);

  const clickOutsideCloseMonthPicker = (e: MouseEvent | TouchEvent) => {
    const rsPickerPopup = document.getElementsByClassName('rs-picker-popup')[0];
    if (rsPickerPopup && rsPickerPopup.contains(e.target as any)) return;
    setIsMonthPickerOpen(false);
  };
  useClickOutside([monthPickerBtnRef], clickOutsideCloseMonthPicker, isMonthPickerOpen);
  
  const { start, end, month, year } = appliedRanges;
  const isZatcaSelected = type?.value === 'zatca';
  const dataPickerLang = lang === 'en' ? enUS : arEG;
  const monthPickerValue = month && year ? new Date(year, month - 1) : null;
  const monthPickerBtnText = month && year ? new Date(year, month - 1)?.toLocaleDateString(lang, { month: 'long', year: 'numeric' }) : undefined;
  
  const {
    data: columnsData,
    isLoading: isLoadingCustomColumns,
    isError: isColumnsError,
    error: columnsError,
    isFetching: isColumnsFetching
  } = useQuery<TCustomReportColumnsResponse>({
    queryKey: [QUERY_KEYS.GET_CUSTOM_REPORT_COLUMNS, isLive, lang],
    queryFn: () => getCustomReportColumns({ isLive }).then(res => res.data),
  });

  const transformedColumns: TTransformedColumn[] = columnsData?.data.map(column => ({
    id: column.id,
    label: column.title,
    value: column.column_name,
  })) || [];

  const {
    mutate: exportReportMutate,
    isPending: isExportLoading,
    isError: isExportError,
    error: exportError,
    data: exportResponse
  } = useMutation({
    mutationFn: (params: TGenerateReportAPIParams) =>
      exportReport(params, isLive),
    mutationKey: [QUERY_KEYS.EXPORT_REPORT],
    onSuccess: () => {
      const payload = generationPayloadRef.current;
      const generationTimeMs = generationStartTimeRef.current != null
        ? Math.round(Date.now() - generationStartTimeRef.current)
        : undefined;
      amplitude.track(REPORTS_STATEMENTS_EVENTS.REPORT_GENERATION_SUCCEEDED, {
        ...(payload && {
          [REPORTS_STATEMENTS_EVENTS_PARAMS.REPORT_TYPE]: payload.report_type,
          [REPORTS_STATEMENTS_EVENTS_PARAMS.FORMAT]: payload.format,
        }),
        ...(generationTimeMs !== undefined && { 
          [REPORTS_STATEMENTS_EVENTS_PARAMS.GENERATION_TIME_MS]: generationTimeMs,
        }),
      });
      generationStartTimeRef.current = null;
      generationPayloadRef.current = null;
      if(isReportsTemplateOpen) setIsReportsTemplateOpen(false)
      setIsShareModalOpen(true);
        queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_REPORTS],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_REPORT_TYPES],
        refetchType: 'all'
      });
      handleResettingExportData();
      formattedSecondaryFilters();
    },
    onError: (error: any) => {
      const payload = generationPayloadRef.current;
      const errorDescription =
        (error &&
          typeof error === 'object' &&
          'response' in error &&
          (error as any).response?.data?.message) ||
        (error as any)?.message ||
        t('errors.somethingWentWrong');

      amplitude.track(REPORTS_STATEMENTS_EVENTS.REPORT_GENERATION_FAILED, {
        ...(payload && {
          [REPORTS_STATEMENTS_EVENTS_PARAMS.REPORT_TYPE]: payload.report_type,
          [REPORTS_STATEMENTS_EVENTS_PARAMS.FORMAT]: payload.format,
        }),
        [REPORTS_STATEMENTS_EVENTS_PARAMS.ERROR_DESCRIPTION]: errorDescription,
      });
      generationStartTimeRef.current = null;
      generationPayloadRef.current = null;
    },
  });

  const {
    data: secondaryFiltersData,
    isLoading: isSecondaryFiltersLoading,
    isError: isSecondaryFiltersError,
    error: secondaryFiltersError
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_REPORTS_FILTERS, isLive, lang],
    queryFn: () => getReportSecondaryFilters({ isLive }).then(res => res.data),
  });

  const formattedSecondaryFilters = () => {
    if (secondaryFiltersData && secondaryFiltersData.data) {
      const apiData = secondaryFiltersData.data;
      const filtersMap: TSecondaryFilters = {};
      Object.entries(apiData).forEach(([key, options]) => {
        const allItemNames = ['all_methods', 'all_channels', 'all_status', 'all_devices'];
        const filteredOptions = (options as any[]).filter(opt =>
          !allItemNames.includes(opt.internal_name)
        );
        filtersMap[key] = {
          id: key,
          label: SECONDARY_FILTERS[key as keyof typeof SECONDARY_FILTERS].label,
          allLabel: SECONDARY_FILTERS[key as keyof typeof SECONDARY_FILTERS].allLabel,
          placeholder: SECONDARY_FILTERS[key as keyof typeof SECONDARY_FILTERS].placeholder,
          options: filteredOptions.map(opt => ({
            label: opt.display_name,
            value: opt.internal_name
          })),
          selectedOptions: filteredOptions.map(opt => ({
            label: opt.display_name,
            value: opt.internal_name
          })),
        };
      });
      setSecondaryFilters(filtersMap);
    }
  };

  useEffect(() => {
    formattedSecondaryFilters();
  }, [secondaryFiltersData, type, headerDateRange]);

  useEffect(() => {
    handleResettingExportData();
  }, [lang]);

  useEffect(() => {
    if(!isShareModalOpen) {
      setSharedList([]);
    }
  }, [isShareModalOpen]);

  const hasError = isReportTypesError || isColumnsError || isExportError || isSecondaryFiltersError || isReportDownloadError;

  useEffect(() => {
    if (hasError) {
      const extractErrorMessage = (error: any): string => {
        return (error && 
          typeof error === 'object' && 
          'response' in error && 
          error.response?.data?.message) || t('errors.somethingWentWrong');
      };

      let errorMessage = '';
      let trackingCode = '';      
      
      if (isReportTypesError) {
        errorMessage = extractErrorMessage(reportTypesError);
        trackingCode = SYSTEM_EVENTS_GROUPING_CODES.REPORTS.REPORTS_TAB.TYPES;
      }
      if (isReportDownloadError) {
        errorMessage = extractErrorMessage(reportDownloadError);
        trackingCode = SYSTEM_EVENTS_GROUPING_CODES.REPORTS.REPORTS_TAB.DOWNLOAD;
      }
      if (isColumnsError) {
        errorMessage = extractErrorMessage(columnsError);
        trackingCode = SYSTEM_EVENTS_GROUPING_CODES.REPORTS.REPORTS_TAB.COLUMNS;
      }
      if (isExportError) {
        errorMessage = extractErrorMessage(exportError);
        trackingCode = SYSTEM_EVENTS_GROUPING_CODES.REPORTS.REPORTS_TAB.EXPORT;
      }
      if (isSecondaryFiltersError) {
        errorMessage = extractErrorMessage(secondaryFiltersError);
        trackingCode = SYSTEM_EVENTS_GROUPING_CODES.REPORTS.REPORTS_TAB.SECONDARY_FILTER;
      }      
      notify.error(errorMessage, {
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'error',
            trackingCode,
            msg: errorMessage,
            srcFeature: SYSTEM_EVENTS_SRC_FEATURES.REPORTS.REPORTS_TAB,
          }),
        timeout: 2,
      });
    }
  }, [isReportTypesError, isColumnsError, isExportError, isSecondaryFiltersError]);

  useEffect(() => {
    if (isCustomReportModalOpen) {
      const { report_name, selected_columns, ranges: customReportRanges } = customReportData ?? {};
      const isValid = report_name && selected_columns.length >= 2 && customReportRanges.start && customReportRanges.end;
      handleCustomReportValidityChange(!!isValid);
    } else {
      document.documentElement.style.overflow = '';
    }
  }, [customReportData]);

  const handleResettingExportData = () => {
    setType(undefined);
    setCustomReportData(prev => ({
      ...customReportDataInitialState,
      ranges: prev.ranges
    }));
  };

  const handleDateRangeChange = (
    newRanges: TAppliedRanges,
    isCustomReport: boolean = false
  ) => {
    if (isCustomReport) {
      setCustomReportData(prev => ({
        ...prev,
        ranges: newRanges
      }));
    } else {
      setAppliedRanges(() => ({
        ...newRanges,
      }));
    }
  };

  const handleTypeChange = (type: TOption) => {
    setType(type);
  };

  const handleCustomReportClick = () => {
    setType({
      label: t('innerLayout.reportsPage.reportTypesFilter.custom'),
      value: 'custom',
    });
    setIsCustomReportModalOpen(true);
  };

  const handleCustomReportClose = () => {
    setIsCustomReportModalOpen(false);
    setCustomReportData(customReportDataInitialState);
    setType(undefined);
  };

  const handleGenerateCustomReport = () => {
    const { report_name, selected_columns, save_template, ranges } = customReportData;

    if (ranges.start && ranges.end) {
      const formattedDateRange = getFormattedDates([ranges.start, ranges.end] as [Date, Date]);
      const dataObj: TGenerateReportAPIParams = {
        report_type: 'custom',
        start_date: formattedDateRange.start_date,
        end_date: formattedDateRange.end_date,
        report_name,
        columns: selected_columns.map(col => col.value),
        save_template,
      };

      const dateRangeName = ranges.name ?? REPORTS_STATEMENTS_EVENTS_VALUES.DATE_RANGE.CUSTOM;
      generationPayloadRef.current = {
        [REPORTS_STATEMENTS_EVENTS_PARAMS.REPORT_TYPE]: REPORTS_STATEMENTS_EVENTS_VALUES.GENERATION_REPORT_TYPE.CUSTOM,
        [SHARED_EVENTS_PARAMS.DATE_RANGE]: dateRangeName,
        [REPORTS_STATEMENTS_EVENTS_PARAMS.FORMAT]: REPORTS_STATEMENTS_EVENTS_VALUES.FILE_FORMAT.XLSX
      };
      generationStartTimeRef.current = Date.now();
      exportReportMutate(dataObj);
      setIsCustomReportModalOpen(false);
    }
  };

  const handleUpdateFilters = (updatedFilters: TSecondaryFilters) => {
    setSecondaryFilters(updatedFilters);
  };

  const handleCustomReportValidityChange = (isValid: boolean) => {
    setIsCustomReportValid(isValid);
  };

  const submitReportHandler = (event?: FormEvent<HTMLFormElement>, templateType?: TReportTemplateTypes) => {
    event?.preventDefault();

    const { start, end, name, month, year } = appliedRanges;
    
    const isReportValid = (isZatcaSelected ? month && year : start && end) && type?.value;
    if (isReportValid) {

      const formattedDateRange = isZatcaSelected ? undefined : getFormattedDates([start, end] as [Date, Date]);

      const secondaryFiltersPayload: Record<string, (string|number)[] | string> = {};
      Object.entries(secondaryFilters).forEach(([key, filter]) => {
        secondaryFiltersPayload[key] =
          (filter.selectedOptions.length === filter.options.length) ?
            ["all"] :
            filter.selectedOptions.map(opt => opt.value);
      });
      const dataObj: TGenerateReportAPIParams = {
        report_type: type.value,
        ...(isZatcaSelected ? {
          month,
          year,
        } : {
          start_date: formattedDateRange?.start_date,
          end_date: formattedDateRange?.end_date,
        }),
        ...(type.value === 'custom' && type.id ? {
          custom_report_id: type.id,
        } : {}),
        ...(templateType ? { template: templateType } : {}),
        ...secondaryFiltersPayload,
      };
      const dateRangeName = name ?? REPORTS_STATEMENTS_EVENTS_VALUES.DATE_RANGE.CUSTOM;
      generationPayloadRef.current = { 
        [REPORTS_STATEMENTS_EVENTS_PARAMS.REPORT_TYPE]: type.label ?? type.value, 
        [SHARED_EVENTS_PARAMS.DATE_RANGE]: dateRangeName, 
        [REPORTS_STATEMENTS_EVENTS_PARAMS.FORMAT]: REPORTS_STATEMENTS_EVENTS_VALUES.FILE_FORMAT.XLSX 
      };
      generationStartTimeRef.current = Date.now();
      exportReportMutate(dataObj);
    }
  };

  const handleChooseReportTemplate = (templateType: TReportTemplateTypes) => {
    submitReportHandler(undefined, templateType);
  };

  const handleGenerateReportClick = (e: FormEvent<HTMLFormElement>) => {
    if (type?.supportsTemplateChoice) {
      setIsReportsTemplateOpen(true);
    } else {
      submitReportHandler(e)
    }
  }

  const renderSubmitButton = () => {
  const isButtonDisabled = (isZatcaSelected ? !month || !year : !start || !end) || !type?.value || isExportLoading;
    return (
    <div className={styles['filters-section__row__submit-button']}>
      <Button
        onClickHandler={handleGenerateReportClick}
        disabled={isButtonDisabled}
        leadingIcon={isExportLoading ? AnimatedSpinner : undefined}
        text={isExportLoading ? t('innerLayout.reportsPage.generating') : t('innerLayout.reportsPage.generateReport')}
        size="md"
      />
    </div>
  )};

  const handleMonthChange = (value: Date | null) => {
    if (!value) return;
    
    const month = value.getMonth() + 1;
    const year = value.getFullYear();

    handleDateRangeChange({
      month,
      year,
    }, false);

    setIsMonthPickerOpen(false);
  };

  const typeDropdownText = type ? type.label : t('innerLayout.reportsPage.reportTypesFilter.title');
  const isGenerateLoading = isColumnsFetching || isReportTypesLoading || isSecondaryFiltersLoading;

  if (isGenerateLoading) {
    return (
      <div className={styles['filters-section']}>
        <div className={styles['filters-section__row']}>
          <SkeletonBase height="2.5rem" />
        </div>
      </div>
    );
  }

  const MODAL_HANDLERS: Record<string, () => ReactNode> = {
    [MODAL_TYPES.CUSTOM_REPORT]: () => (
      <RenderCustomReportsModalBody
        customReportData={customReportData}
        setCustomReportData={setCustomReportData}
        transformedColumns={transformedColumns}
        lang={lang}
        handleDateRangeChange={handleDateRangeChange}
        containerRef={containerRef}
      />
    ),
    [MODAL_TYPES.SHARE]: () => (
      <RenderShareModalBody
        exportResponse={exportResponse}
        sharedList={sharedList}
        setSharedList={setSharedList}
      />
    ),
  };

  const renderModalContent = (modalType: keyof typeof MODAL_HANDLERS) => {
    const handler = MODAL_HANDLERS[modalType];
    return handler ? handler() : null;
  };

  return (
    <div className={styles['filters-section']}>
      <div className={styles['filters-section__error']}>
        
      </div>
      <div className={styles['filters-section__row']}>
        <div className={styles['filters-section__row__type-dropdown']}>
          <Dropdown label={t('innerLayout.reportsPage.type')}>
            <DropdownButton
              type="input"
              text={typeDropdownText}
              placeholder={t('innerLayout.reportsPage.reportTypesFilter.title')}
              disabled={isExportLoading}
            />
            <DropdownMenu>
              <div className={styles['filters-section__row__type-dropdown__custom-report']}>
                <Button
                  text={t('innerLayout.reportsPage.reportTypesFilter.custom')}
                  variant="link"
                  leadingIcon={Plus}
                  onClickHandler={handleCustomReportClick}
                  disabled={isExportLoading || isColumnsError}
                />
              </div>
              {typeOptions.map((option: TTypeSelectorOptions) => (
                <DropdownMenuItem
                  text={option.label}
                  key={`report-type-option-${option.id}`}
                  onClickHandler={() => handleTypeChange(option)}
                  icon={option.icon}
                  disabled={isExportLoading || isReportTypesError}
                />
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
        <div className={styles['filters-section__row__date-range']}>
          {isZatcaSelected ?
            <div className={styles['filters-section__row__date-range__monthly-picker']}>
              <DateRangePickerButton
                ref={monthPickerBtnRef}
                label={t('innerLayout.reportsPage.dateRangePicker.label')}
                placeholder={t('innerLayout.reportsPage.dateRangePicker.monthPlaceholder')}
                text={monthPickerBtnText}
                disabled={isExportLoading || isReportTypesError}
                onClickHandler={() => setIsMonthPickerOpen((prev) => !prev)}
              />
              <CustomProvider locale={dataPickerLang}>
                <DatePicker
                  open={isMonthPickerOpen}
                  menuStyle={{marginTop:"0.25rem", paddingTop: "0.625rem", paddingBottom: "0" }}
                  oneTap
                  shouldDisableDate={shouldDisableZatcaMonth}
                  format="yyyy-MM"
                  container={containerRef?.current || undefined}
                  disabled={isExportLoading || isReportTypesError}
                  placeholder={t('innerLayout.reportsPage.dateRangePicker.monthPlaceholder')}
                  value={monthPickerValue}
                  onChange={(value) => {
                    handleMonthChange(value);
                  }}
                />
              </CustomProvider>
            </div>
            :
          <DateRanges
            appliedRanges={appliedRanges}
            datePickerLabel={t('innerLayout.reportsPage.dateRangePicker.label')}
            handleRangesChange={(ranges) =>
              handleDateRangeChange(ranges, false)
            }
            lang={lang}
            disabled={isExportLoading || isReportTypesError}
          />
          }
        </div>
        {!isMobile && renderSubmitButton()}
      </div>
      {type?.hasFilters && (
        <SecondaryFilters
          secondaryFilters={secondaryFilters}
          updateFilters={handleUpdateFilters}
          type={type}
          disabled={isSecondaryFiltersError || isReportTypesError}
        />
      )}
      <div className={styles['filters-section__row']}>
        {isMobile && renderSubmitButton()}
      </div>

      {isCustomReportModalOpen && (
        <Modal
          open={isCustomReportModalOpen}
          onCloseHandler={handleCustomReportClose}
          title={t('innerLayout.reportsPage.customReport.title')}
          headerIcon={ClipboardListCheck}
          state='primary'
          isMobile={isMobile}
          btnsProps={[
            {
              text: t('innerLayout.reportsPage.generate'),
              variant: 'contained',
              onClickHandler: handleGenerateCustomReport,
              disabled: !isCustomReportValid || isExportLoading,
            },
            {
              text: t('innerLayout.reportsPage.customReport.cancel'),
              variant: 'outlined',
              onClickHandler: handleCustomReportClose,
              color: "secondary",
              disabled: isExportLoading,
            },
          ]}
          lang={lang}
          containerRef={containerRef}
          wrapperClassName={styles['custom-report-modal']}
        >
          {renderModalContent(MODAL_TYPES.CUSTOM_REPORT)} 
        </Modal>
      )}
      {isShareModalOpen && exportResponse?.data?.data?.report_id && (
        <Modal
          open={isShareModalOpen && exportResponse?.data?.data?.report_id}
          onCloseHandler={() => setIsShareModalOpen(false)}
          hasCloseIcon={true}
          headerIcon={SuccessHeader}
          keepHeaderIconDefaultColors={true}
          title={t("innerLayout.reportsPage.share.title") || "You've Generated a new report."}
          supportingText={t("innerLayout.reportsPage.share.supportingText") || "Please give it a few minutes to be fully ready."}
          width="30rem"
          wrapperClassName={styles['share-report-modal']}
        >
          {renderModalContent(MODAL_TYPES.SHARE)}
        </Modal>
      )}
      <ReportsTemplate
        isOpen={isReportsTemplateOpen}
        handleClose={() => setIsReportsTemplateOpen(false)}
        handleSubmit={handleChooseReportTemplate}
        isLoading={isExportLoading}
      />
    </div>
  );
};
